import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import CircularProgress from 'components/shared/CircularProgress';
import SummarizationActionsMenu from 'components/summarization/SummarizationActionsMenu';
import SummaryJobSubscriber from 'components/summarizations/SummaryJobSubscriber';
import { simpleDateAtTime } from 'lib/date';
import AIJob, { AIJobOptions } from 'models/AIJob';
import Summarization from 'models/Summarization';
import colors from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

const columnHelper = createColumnHelper<Summarization>();

type Props = {
  onClickSummarization: (summarizationId: string) => void;
};

const summarizationsTableColumns = ({ onClickSummarization }: Props) => {
  return [
    columnHelper.accessor((row) => row.title, {
      id: 'title',
      header: 'Title',
      cell: (info) => {
        const summaryJob = new AIJob({ ...info.row.original.latestSummaryJob } as AIJobOptions);
        const { isProcessing } = summaryJob;

        return (
          <SummaryNameContainer>
            <SummaryJobSubscriber job={summaryJob} />
            {isProcessing && (
              <div>
                <CircularProgress size={16} thickness={2} color={colors.blue100} />
              </div>
            )}
            <Link fontWeight='500' onClick={() => onClickSummarization(info.row.original.id)}>
              {info.getValue()}
            </Link>
          </SummaryNameContainer>
        );
      },
      size: 200,
    }),
    columnHelper.accessor((row) => row.data.patient.name, {
      id: 'patientName',
      header: 'Patient Name',
      cell: (info) => info.getValue() || '—',
      size: 100,
    }),
    columnHelper.accessor((row) => row.attachmentCount, {
      id: 'sourcesCount',
      header: '# of Sources',
      cell: (info) => info.getValue(),
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor((row) => row.createdAt, {
      id: 'createdAt',
      header: 'Created At',
      cell: (info) => simpleDateAtTime(info.getValue() ?? ''),
      size: 100,
    }),
    columnHelper.display({
      id: 'menu',
      cell: (info) => <SummarizationActionsMenu summarization={info.row.original} inline />,
      size: 75,
      meta: { style: { justifyContent: 'flex-end' } },
    }),
  ];
};

export default summarizationsTableColumns;

const SummaryNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Link = styled(BodySmall)`
  cursor: pointer;
  color: ${() => colors.blue100};
`;
